import { useState, useEffect } from "react";

const SESSION_KEY = "mc_auth_v1";

const G = {
  root: {
    position: "fixed",
    inset: 0,
    background: "#0a0a0c",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Mono', 'Courier New', monospace",
    zIndex: 9999,
  },
  card: {
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 16,
    padding: "36px 40px",
    width: 360,
    boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
  },
  title: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 26,
    fontWeight: 600,
    color: "#f0ede6",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: 10,
    color: "#444",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginTop: 4,
    marginBottom: 28,
  },
  label: {
    display: "block",
    fontSize: 10,
    color: "#555",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 5,
  },
  input: {
    width: "100%",
    background: "#0f0f11",
    border: "1px solid #2a2a2e",
    borderRadius: 8,
    padding: "10px 12px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    boxSizing: "border-box",
    outline: "none",
    marginBottom: 14,
  },
  btn: {
    width: "100%",
    background: "#1e1e22",
    border: "1px solid #3a3a3e",
    borderRadius: 8,
    padding: "11px",
    color: "#f0ede6",
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    cursor: "pointer",
    letterSpacing: "0.06em",
    marginTop: 6,
    transition: "background 0.15s",
  },
  error: {
    fontSize: 11,
    color: "#E24B4A",
    marginTop: 10,
    textAlign: "center",
    minHeight: 16,
  },
  // PIN step
  pinDots: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    marginBottom: 28,
  },
  dot: (filled) => ({
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: filled ? "#f0ede6" : "transparent",
    border: "1px solid " + (filled ? "#f0ede6" : "#333"),
    transition: "background 0.1s",
  }),
  numpad: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8,
  },
  key: (accent) => ({
    background: accent ? "#1D9E75" : "#1a1a1d",
    border: "1px solid " + (accent ? "#1D9E75" : "#2a2a2e"),
    borderRadius: 8,
    padding: "14px 0",
    color: accent ? "#fff" : "#b8b5ae",
    fontSize: accent === "del" ? 18 : 18,
    fontFamily: "'DM Mono', monospace",
    cursor: "pointer",
    textAlign: "center",
    transition: "background 0.1s",
    userSelect: "none",
  }),
  backLink: {
    background: "none",
    border: "none",
    color: "#444",
    fontSize: 10,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    cursor: "pointer",
    marginTop: 20,
    display: "block",
    textAlign: "center",
    width: "100%",
  },
};

const PIN_LENGTH = parseInt(process.env.REACT_APP_PIN_LENGTH || "4");
const PIN_SESSION_KEY = "mc_finance_v1";

function CredentialsGate({ children }) {
  const [authed, setAuthed] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [shaking, setShaking] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === "1") setAuthed(true);
  }, []);

  function shake() { setShaking(true); setTimeout(() => setShaking(false), 400); }

  function submit(e) {
    e.preventDefault();
    if (username === process.env.REACT_APP_AUTH_USERNAME && password === process.env.REACT_APP_AUTH_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, "1");
      setAuthed(true);
    } else {
      setError("Invalid username or password.");
      shake();
      setPassword("");
    }
  }

  if (authed) return children;

  return (
    <>
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}}.shake{animation:shake 0.4s ease}`}</style>
      <div style={G.root}>
        <div className={shaking ? "shake" : ""} style={G.card}>
          <p style={G.title}>Welcome Back</p>
          <p style={G.subtitle}>Sign in to continue</p>
          <form onSubmit={submit} autoComplete="off">
            <label style={G.label}>Username</label>
            <input style={G.input} value={username} onChange={e => setUsername(e.target.value)} autoFocus spellCheck={false} />
            <label style={G.label}>Password</label>
            <input style={G.input} type="password" value={password} onChange={e => setPassword(e.target.value)} />
            <button style={G.btn} type="submit">Continue →</button>
            <p style={G.error}>{error}</p>
          </form>
        </div>
      </div>
    </>
  );
}

function PinGate({ children }) {
  const [authed, setAuthed] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shaking, setShaking] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(PIN_SESSION_KEY) === "1") setAuthed(true);
  }, []);

  function shake() { setShaking(true); setTimeout(() => setShaking(false), 400); }

  function pressKey(digit) {
    if (pin.length < PIN_LENGTH) {
      const next = pin + digit;
      setPin(next);
      if (next.length === PIN_LENGTH) {
        if (next === process.env.REACT_APP_AUTH_PIN) {
          sessionStorage.setItem(PIN_SESSION_KEY, "1");
          setAuthed(true);
        } else {
          setError("Incorrect PIN.");
          shake();
          setTimeout(() => setPin(""), 400);
        }
      }
    }
  }

  function delKey() { setPin(p => p.slice(0, -1)); setError(""); }

  if (authed) return children;

  return (
    <>
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}}.shake{animation:shake 0.4s ease}`}</style>
      <div style={G.root}>
        <div className={shaking ? "shake" : ""} style={G.card}>
          <p style={G.title}>Finance</p>
          <p style={G.subtitle}>Enter your PIN</p>
          <div style={G.pinDots}>
            {Array.from({ length: PIN_LENGTH }).map((_, i) => (
              <div key={i} style={G.dot(i < pin.length)} />
            ))}
          </div>
          <div style={G.numpad}>
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <button key={n} style={G.key(false)} onClick={() => pressKey(String(n))}>{n}</button>
            ))}
            <button style={G.key(false)} onClick={delKey}>⌫</button>
            <button style={G.key(false)} onClick={() => pressKey("0")}>0</button>
            <div />
          </div>
          <p style={G.error}>{error}</p>
        </div>
      </div>
    </>
  );
}

export default function LoginGate({ children, pinOnly = false }) {
  if (pinOnly) return <PinGate>{children}</PinGate>;
  return <CredentialsGate>{children}</CredentialsGate>;
}
